import { ApiError } from 'api/schema/core/ApiError'
import { StationApi } from 'api/StationApi'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { IStationControlBoard, IStationLotGate, IStationSoftwareStatus } from 'utils/interfaces'
import { getStationFeedbackStatus } from 'utils/stationFeedback'
import type { IStation } from 'utils/interfaces'

import { buildStationLotGate } from './stationLotGate'

const POLL_INTERVAL_MS = 3000
/** After Send, wait for telemetry IN_PROGRESS before unlocking CTRL/Release. */
const AWAITING_IN_PROGRESS_TIMEOUT_MS = 12000

type RunOpOptions = {
  /** After success, keep UI locked until lotGate.inProgress (stage Send). */
  awaitInProgress?: boolean
  /** Merge into board.software after a successful OTA trigger. */
  patchSoftware?: (prev: IStationSoftwareStatus) => IStationSoftwareStatus
  /** Prefix for inline Control error, e.g. "Occupy failed". */
  errorPrefix?: string
}

function formatOpError(error: unknown, prefix?: string): string {
  let detail = ''
  if (error instanceof ApiError) {
    const body = error.body
    if (body && typeof body === 'object' && typeof (body as { detail?: unknown }).detail === 'string') {
      detail = String((body as { detail: string }).detail).trim()
    }
    if (!detail && typeof error.message === 'string') {
      detail = error.message.trim()
    }
  } else if (error instanceof Error) {
    detail = error.message.trim()
  }
  if (!detail) {
    detail = 'Request failed'
  }
  return prefix ? `${prefix}: ${detail}` : detail
}

/**
 * Single poll + shared locks for Station → Control.
 * SSOT for lot flags is the board (one snapshot); awaitingInProgress only bridges MQTT→telemetry lag.
 */
export function useStationControlBoard(station: IStation) {
  const feedback = getStationFeedbackStatus(station)
  const [board, setBoard] = useState<IStationControlBoard | null>(null)
  const [opsBusy, setOpsBusy] = useState(false)
  const [awaitingInProgress, setAwaitingInProgress] = useState(false)
  const [lastOpError, setLastOpError] = useState<string | null>(null)
  const awaitingRef = useRef(false)

  const reload = useCallback(async () => {
    if (!station.id) return
    try {
      setBoard(await StationApi.fetchControlBoard(station.id))
    } catch (error) {
      console.warn('[useStationControlBoard] fetch failed', error)
    }
  }, [station.id])

  useEffect(() => {
    setLastOpError(null)
  }, [station.id])

  useEffect(() => {
    if (feedback === 'no_feedback') {
      setBoard(null)
      setAwaitingInProgress(false)
      awaitingRef.current = false
      setLastOpError(null)
      return
    }
    void reload()
    const timer = window.setInterval(() => void reload(), POLL_INTERVAL_MS)
    return () => window.clearInterval(timer)
  }, [feedback, reload])

  const lotGate: IStationLotGate | null = useMemo(() => buildStationLotGate(board), [board])

  const softwareUpdateInProgress = Boolean(board?.software?.update_in_progress)

  useEffect(() => {
    if (!awaitingInProgress) return
    if (lotGate?.inProgress) {
      setAwaitingInProgress(false)
      awaitingRef.current = false
    }
  }, [awaitingInProgress, lotGate?.inProgress])

  useEffect(() => {
    if (!awaitingInProgress) return
    const timer = window.setTimeout(() => {
      setAwaitingInProgress(false)
      awaitingRef.current = false
    }, AWAITING_IN_PROGRESS_TIMEOUT_MS)
    return () => window.clearTimeout(timer)
  }, [awaitingInProgress])

  /** Shared lock for STEPS / CTRL / Software while any op or OTA is running. */
  const uiLocked = opsBusy || awaitingInProgress || softwareUpdateInProgress

  const runOp = useCallback(
    async (action: () => Promise<unknown>, options?: RunOpOptions) => {
      if (!station.id || opsBusy || awaitingRef.current) return
      // OTA in progress: block STEPS/CTRL; Update itself is already disabled via uiLocked.
      if (softwareUpdateInProgress && !options?.patchSoftware) return
      setOpsBusy(true)
      setLastOpError(null)
      try {
        await action()
        if (options?.awaitInProgress) {
          awaitingRef.current = true
          setAwaitingInProgress(true)
        }
        if (options?.patchSoftware) {
          setBoard(prev =>
            prev
              ? {
                  ...prev,
                  software: options.patchSoftware!(prev.software),
                }
              : prev
          )
        }
        await reload()
      } catch (error) {
        setLastOpError(formatOpError(error, options?.errorPrefix))
      } finally {
        setOpsBusy(false)
      }
    },
    [station.id, opsBusy, reload, softwareUpdateInProgress]
  )

  return {
    feedback,
    board,
    lotGate,
    opsBusy,
    awaitingInProgress,
    softwareUpdateInProgress,
    uiLocked,
    lastOpError,
    reload,
    runOp,
  }
}
